'use client'

import { LayoutGroup, m, useReducedMotion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'

import { RevealRule } from '@/features/landing/shared/reveal.client'

export interface PathItem {
    n: string
    title: string
    kicker: string
    copy: string
}

/**
 * Animated path list with a single traveling vermilion cursor.
 *
 * On hover devices the cursor follows the mouse. On hover-less devices
 * (where hover styles only fire on tap) it follows the scroll instead:
 * an IntersectionObserver band across the middle of the viewport marks
 * the row passing through it as active.
 *
 * The cursor sits as a SIBLING of the RevealRule wrapper, not inside it,
 * because RevealRule uses clip-path: inset(...) which would clip the
 * cursor's left:-20px positioning out of view.
 */
export function PathList({ paths }: { paths: readonly PathItem[] }) {
    const reduce = useReducedMotion()
    const [active, setActive] = useState<number | null>(null)
    const rows = useRef<Array<HTMLLIElement | null>>([])

    useEffect(() => {
        if (!window.matchMedia('(hover: none)').matches) return
        // Rows currently inside the band; the topmost wins so the cursor
        // walks the list one row at a time. The band is the middle fifth
        // of the viewport (insets of 40% top and bottom). The rows are
        // contiguous, so an empty band means the band sits entirely above
        // or below the list — the cursor goes dark outside the section
        // and re-lights on the way back through.
        const inBand = new Set<number>()
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const i = rows.current.indexOf(
                        entry.target as HTMLLIElement
                    )
                    if (i < 0) continue
                    if (entry.isIntersecting) inBand.add(i)
                    else inBand.delete(i)
                }
                setActive(inBand.size > 0 ? Math.min(...inBand) : null)
            },
            { rootMargin: '-40% 0px -40% 0px' }
        )
        for (const el of rows.current) if (el) observer.observe(el)
        return () => observer.disconnect()
    }, [])

    return (
        <LayoutGroup>
            <ol
                aria-label="Four progressive learning paths"
                className="mx-auto max-w-5xl border-t border-ink/15"
                onMouseLeave={() => setActive(null)}
            >
                {paths.map((path, i) => {
                    const isActive = active === i
                    return (
                        <li
                            key={path.n}
                            ref={(el) => {
                                rows.current[i] = el
                            }}
                            className="relative border-b border-ink/15"
                        >
                            {/* Cursor — outside RevealRule so its clip-path doesn't hide us */}
                            {isActive && (
                                <m.span
                                    layoutId="path-cursor"
                                    aria-hidden
                                    className="pointer-events-none absolute top-0 bottom-0 left-[-12px] z-10 w-[3px] bg-vermilion md:left-[-20px]"
                                    transition={
                                        reduce
                                            ? { duration: 0 }
                                            : {
                                                  duration: 0.45,
                                                  ease: [0.22, 1, 0.36, 1],
                                              }
                                    }
                                />
                            )}

                            <RevealRule delay={i * 0.06}>
                                <div
                                    onMouseEnter={() => setActive(i)}
                                    className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 py-8 md:grid-cols-[7rem_18rem_1fr] md:items-center md:gap-x-10 md:py-10"
                                >
                                    <div
                                        className={
                                            isActive
                                                ? 'font-serif text-[64px] leading-[0.85] tracking-[-0.04em] text-ink transition-colors duration-300 md:text-[88px]'
                                                : 'font-serif text-[64px] leading-[0.85] tracking-[-0.04em] text-ink/30 transition-colors duration-300 md:text-[88px]'
                                        }
                                    >
                                        {path.n}
                                    </div>

                                    <div>
                                        <h3 className="font-serif text-[28px] leading-tight tracking-[-0.015em] text-ink md:text-[34px]">
                                            {path.title}
                                        </h3>
                                        <p
                                            className={
                                                isActive
                                                    ? 'mt-2 min-h-[2.6em] font-serif text-[12px] tracking-[0.16em] text-ink uppercase transition-colors duration-300'
                                                    : 'mt-2 min-h-[2.6em] font-serif text-[12px] tracking-[0.16em] text-muted-fg uppercase transition-colors duration-300'
                                            }
                                        >
                                            {path.kicker}
                                        </p>
                                    </div>

                                    <div className="col-span-2 border-ink/10 md:col-span-1 md:border-l md:pl-10">
                                        <p className="max-w-prose text-[14.5px] leading-[1.7] text-body-fg">
                                            {path.copy}
                                        </p>
                                    </div>
                                </div>
                            </RevealRule>
                        </li>
                    )
                })}
            </ol>
        </LayoutGroup>
    )
}
